import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../utils/auth";
import { Card, Table, Button, Form, Modal, Alert, Pagination } from "react-bootstrap";
import { IconCirclePlus, IconUserPlus, IconTrash, IconEdit, IconLock, IconLockOpen, IconCaretLeft, IconCopy, IconRefresh, IconPower } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", firstname: "", lastname: "", country: "" });
  const [selectedUser, setSelectedUser] = useState(null);
  const navigate = useNavigate();
  const [country, setCountry] = useState("");
  const [creditAmount, setCreditAmount] = useState(0);
  const [error, setError] = useState("");
  const [copiedUserId, setCopiedUserId] = useState(null);
  const [editUserModal, setEditUserModal] = useState(false);
  const [editUserData, setEditUserData] = useState({ id: null, firstname: "", lastname: "", country: "", password: "" });
  const [isSubtract, setIsSubtract] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // 🔎 Поиск
  const filteredUsers = users
    .filter((user) => user.email?.toLowerCase().includes(searchTerm.toLowerCase()));

  // 📄 Пагинация должна считать по отфильтрованным!
  const indexOfLast = currentPage * usersPerPage;
  const indexOfFirst = indexOfLast - usersPerPage;
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage) || 1;
  const currentUsers = filteredUsers.slice(indexOfFirst, indexOfLast);

  const getPaginationPages = (current, total) => {
    const delta = 2;
    const pages = new Set();
    pages.add(1);
    pages.add(total);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      result.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  const sortByCreatedDesc = (arr) =>
    [...arr].sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da; // новые первыми
    });

  const fetchUsers = async () => {
    try {
      const response = await fetchWithAuth("/api/auth/users");
      const data = await response.json();
      setUsers(sortByCreatedDesc(data)); // ✅ сортируем сразу
      setCurrentPage(1);
    } catch (err) {
      console.error("Ошибка загрузки пользователей:", err);
      setError("Ошибка загрузки пользователей");
    }
  };

  // ✅ Загружаем и сортируем по умолчанию по дате регистрации (новые сверху)
  useEffect(() => {
    fetchWithAuth("/api/auth/users")
      .then((res) => res.json())
      .then((data) => {
        // Лог оставить можно, но убрал шум — при необходимости включишь
        // console.log("👀 Данные пользователей:", data);
        setUsers(sortByCreatedDesc(data));
        setCurrentPage(1);
      })
      .catch(() => setError("Ошибка загрузки пользователей"));
  }, []);

  const toggleApi = async (userId, enabled) => {
    try {
      await fetchWithAuth("/api/admin/toggle-api", {
        method: "POST",
        body: JSON.stringify({ user_id: userId, enabled }),
      });
      fetchUsers(); // обновим
    } catch (err) {
      console.error("Error toggling API:", err);
    }
  };

  const copyToClipboard = async (text, userId) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const temp = document.createElement("input");
        temp.value = text;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand("copy");
        document.body.removeChild(temp);
      }
      setCopiedUserId(userId);
      setTimeout(() => setCopiedUserId(null), 2000);
    } catch (_) {
      // молча
    }
  };

  const regenerateApi = async (userId) => {
    try {
      const res = await fetchWithAuth("/api/admin/regenerate", {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json();
      copyToClipboard(data.api_key, userId);
      fetchUsers(); // обновим
    } catch (err) {
      console.error("Error regenerating API key:", err);
    }
  };

  const openCreditModal = (user) => {
    setIsSubtract(false); // сбрасываем всегда
    setSelectedUser(user);
    setShowModal(true);
  };

  const addCredits = async (userId) => {
    if (creditAmount <= 0) {
      setError("Сумма должна быть больше 0!");
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/auth/users/${userId}/credits`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: creditAmount }),
      });

      if (response.ok) {
        const data = await response.json();
        setUsers((prev) =>
          sortByCreatedDesc(
            prev.map((u) => (u.id === userId ? { ...u, credits: data.credits } : u))
          )
        );
        setCreditAmount(0);
      } else {
        setError("Ошибка при обновлении кредитов");
      }
    } catch (err) {
      console.error("Ошибка пополнения кредитов:", err);
    }
  };

  const addUser = async () => {
    try {
      const response = await fetchWithAuth("/api/auth/admin/create-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newUser),
      });

      if (response.ok) {
        setShowModal(false);
        setNewUser({ email: "", password: "", firstname: "", lastname: "", country: "" });
        fetchUsers(); // перезагрузим и отсортируем
      } else {
        setError("Ошибка при добавлении пользователя");
      }
    } catch (err) {
      console.error("Ошибка добавления пользователя:", err);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Вы уверены, что хотите удалить пользователя?")) return;
    try {
      const response = await fetchWithAuth(`/api/auth/users/${userId}`, { method: "DELETE" });
      if (response.ok) {
        setUsers((prev) => prev.filter((u) => u.id !== userId));
      } else {
        alert("Ошибка при удалении пользователя");
      }
    } catch (error) {
      console.error("Ошибка удаления пользователя:", error);
    }
  };

  const updateCredits = async () => {
    if (!selectedUser || isNaN(creditAmount)) return;

    const amount = isSubtract ? -Math.abs(creditAmount) : Math.abs(creditAmount);

    try {
      await fetchWithAuth(`/api/auth/users/${selectedUser.id}/credits`, {
        method: "PATCH",
        body: JSON.stringify({ amount }),
      });

      setSuccessMessage(`Кредиты успешно ${isSubtract ? "списаны" : "добавлены"} пользователю.`);
      setCreditAmount(0);
      fetchUsers();

      setTimeout(() => {
        setSelectedUser(null);
        setIsSubtract(false);
        setSuccessMessage("");
      }, 10);
    } catch (err) {
      console.error("Ошибка при обновлении кредитов:", err);
    }
  };

  const openEditUserModal = (user) => {
    setEditUserData({ id: user.id, firstname: user.firstname, lastname: user.lastname, country: user.country, password: "" });
    setEditUserModal(true);
  };

  const saveEditUser = async () => {
    try {
      const response = await fetchWithAuth(`/api/auth/users/${editUserData.id}/edit`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editUserData),
      });
      if (response.ok) {
        setEditUserModal(false);
        fetchUsers();
      } else {
        alert("Ошибка при обновлении пользователя");
      }
    } catch (err) {
      console.error("Ошибка при обновлении пользователя:", err);
    }
  };

  const toggleBlockUser = async (userId, isBlocked) => {
    try {
      const response = await fetchWithAuth(`/api/auth/users/${userId}/block`, {
        method: "PATCH",
        body: JSON.stringify({ blocked: !isBlocked }),
      });
      if (response.ok) {
        setUsers((prev) =>
          prev.map((u) => (u.id === userId ? { ...u, blocked: !isBlocked } : u))
        );
      } else {
        alert("Ошибка при изменении статуса пользователя");
      }
    } catch (error) {
      console.error("Ошибка блокировки пользователя:", error);
    }
  };

  return (
    <>
      <Helmet>
        <title>All Users - IndexBooster</title>
      </Helmet>

      <Card>
        <Card.Header className="d-flex align-items-center gap-2">
          {successMessage && (
            <Alert variant="success" onClose={() => setSuccessMessage("")} dismissible>
              {successMessage}
            </Alert>
          )}
          <div className="d-flex align-items-center gap-2 flex-grow-1">
            <Button variant="light" size="sm" onClick={() => navigate(-1)}>
              <IconCaretLeft size={18} />
            </Button>
            <h3 className="card-title">Users</h3>
          </div>
          <div className="d-flex w-100 justify-content-md-end">
            <Form.Control
              type="text"
              placeholder="Search by email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              style={{ maxWidth: "300px" }}
            />
          </div>
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              <IconUserPlus size={18} className="me-2" /> Добавить пользователя
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="border-bottom">
          {error && <Alert variant="danger">{error}</Alert>}
          <div className="table-responsive">
            <Table className="table card-table table-vcenter text-nowrap datatable text-center">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th className="text-center">Имя</th>
                  <th className="text-center">Фамилия</th>
                  <th className="text-center">Страна</th>
                  <th className="text-center">Роль</th>
                  <th className="text-center">Кредиты</th>
                  <th className="text-center">Проекты</th>
                  <th className="text-center">Ссылки</th>
                  <th className="text-center">API Key</th>
                  <th className="text-center">Статус</th>
                  <th className="text-center">Действия</th>
                  <th className="text-center">Дата регистрации</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="text-center">Нет пользователей</td>
                  </tr>
                ) : (
                  currentUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>
                        <Link to={`/user/${user.id}/projects`} className="text-primary">
                          {user.email}
                        </Link>
                      </td>
                      <td className="text-center">{user.firstname}</td>
                      <td className="text-center">{user.lastname}</td>
                      <td className="text-center">{user.country}</td>
                      <td className="text-center">{user.role}</td>
                      <td className="text-center">
                        {user.credits ?? 0}
                        <IconCirclePlus
                          size={18}
                          stroke={2}
                          className="text-success cursor-pointer ms-1"
                          onClick={() => setSelectedUser(user)}
                          title="Пополнить кредиты"
                        />
                      </td>
                      <td className="text-center">{user.projects ?? 0}</td>
                      <td className="text-center">{user.urls ?? 0}</td>
                      <td className="text-center">
                        {user.api_key ? (
                          <div className="d-flex flex-column align-items-center gap-1">
                            <code className="text-monospace" style={{ fontSize: "11px", wordBreak: "break-all" }}>
                              {user.api_key}
                            </code>
                            <div className="d-flex align-items-center gap-2 justify-content-center">
                              <button
                                className={`btn btn-6 ${user.api_enabled ? "btn-primary" : "btn-secondary"}`}
                                style={{ fontSize: "11px", padding: "2px 6px" }}
                                disabled
                              >
                                {user.api_enabled ? "Enabled" : "Disabled"}
                              </button>
                              <button
                                className="btn btn-sm btn-light border"
                                title="Copy"
                                onClick={() => copyToClipboard(user.api_key, user.id)}
                              >
                                {copiedUserId === user.id ? "Copied!" : <IconCopy size={14} />}
                              </button>
                            </div>
                            <div className="d-flex align-items-center gap-2 mt-1 justify-content-center">
                              <button
                                className="btn btn-xs btn-outline-secondary"
                                title="Toggle Enabled"
                                onClick={() => toggleApi(user.id, !user.api_enabled)}
                              >
                                <IconPower size={14} />
                              </button>
                              <button
                                className="btn btn-xs btn-outline-warning"
                                title="Regenerate Key"
                                onClick={() => regenerateApi(user.id)}
                              >
                                <IconRefresh size={14} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted">–</span>
                        )}
                      </td>

                      <td className="text-center">
                        <span
                          className={`status-indicator ${user.blocked ? "bg-danger" : "bg-success"}`}
                          style={{
                            display: "inline-block",
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            marginRight: "8px",
                          }}
                        ></span>
                        {user.blocked ? "" : ""}
                      </td>
                      <td className="text-center">
                        <button className="btn btn-icon btn-outline-danger me-2" onClick={() => deleteUser(user.id)}>
                          <IconTrash size={20} />
                        </button>
                        <button className="btn btn-icon btn-outline-primary me-2" onClick={() => openEditUserModal(user)}>
                          <IconEdit size={20} />
                        </button>
                        <button className="btn btn-icon btn-outline-warning" onClick={() => toggleBlockUser(user.id, user.blocked)}>
                          {user.blocked ? <IconLockOpen size={20} /> : <IconLock size={20} />}
                        </button>
                      </td>
                      <td className="text-center">
                        {user.created_at?.slice(0, 10)}
                        <br />
                        {user.created_at?.slice(11, 19)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>

        {/* Модалка для пополнения кредитов */}
        <Modal show={!!selectedUser} onHide={() => setSelectedUser(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Пополнение кредитов</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Введите сумму</Form.Label>
              <Form.Control
                type="number"
                value={creditAmount}
                onChange={(e) => setCreditAmount(parseFloat(e.target.value))}
              />
            </Form.Group>

            <Form.Check
              type="switch"
              id="switch-subtract"
              label="Списать кредиты вместо добавления"
              checked={isSubtract}
              onChange={(e) => setIsSubtract(e.target.checked)}
              className="mt-3"
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setSelectedUser(null)}>Отмена</Button>
            <Button variant={isSubtract ? "danger" : "success"} onClick={() => updateCredits(selectedUser.id)}>
              {isSubtract ? "Списать" : "Пополнить"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Модалка для добавления пользователя */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Добавить пользователя</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Пароль</Form.Label>
                <Form.Control type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Имя</Form.Label>
                <Form.Control type="text" value={newUser.firstname} onChange={(e) => setNewUser({ ...newUser, firstname: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Фамилия</Form.Label>
                <Form.Control type="text" value={newUser.lastname} onChange={(e) => setNewUser({ ...newUser, lastname: e.target.value })} />
              </Form.Group>
              <Form.Group controlId="country">
                <Form.Label>Страна</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Введите страну"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Отмена</Button>
            <Button variant="primary" onClick={addUser}>Добавить</Button>
          </Modal.Footer>
        </Modal>

        <Card.Footer className="px-4">
          <div className="d-flex flex-column gap-2 w-100">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 w-100">
              <div className="d-flex align-items-center flex-wrap gap-3">
                <Pagination className="m-0 flex-wrap">
                  <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>«</Pagination.Prev>
                  {getPaginationPages(currentPage, totalPages).map((page, i) =>
                    page === "..." ? (
                      <Pagination.Ellipsis key={`ellipsis-${i}`} disabled />
                    ) : (
                      <Pagination.Item key={page} active={page === currentPage} onClick={() => setCurrentPage(page)}>
                        {page}
                      </Pagination.Item>
                    )
                  )}
                  <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>»</Pagination.Next>
                </Pagination>
                <div className="text-muted">
                  Showing <b>{filteredUsers.length === 0 ? 0 : indexOfFirst + 1}</b>–<b>{Math.min(indexOfLast, filteredUsers.length)}</b> of <b>{filteredUsers.length}</b> entries
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 ms-md-auto">
                <span className="text-muted">Show</span>
                <Form.Select
                  value={usersPerPage}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setUsersPerPage(Number(e.target.value));
                  }}
                  size="sm"
                >
                  {[10, 25, 50, 100, 200].map((val) => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </Form.Select>
                <span className="text-muted">entries</span>
              </div>
            </div>
          </div>
        </Card.Footer>
      </Card>
    </>
  );
};

export default Users;
